import { useMemo, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTriggerButton,
  Tooltip,
  useModals,
} from '@customerio/pluma-components/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { exportCustomers, type ExportCustomersInput } from '@/services/db/customer/mutations';
import { exportOther, type ExportOtherInput, type OtherExportType } from '@/services/db/export/mutations';
import { getObjectTypesQueryOptions } from '@/services/db/object-type/queries';
import { useProductTaxonomy } from '@/services/product-taxonomy';
import { isEmptyString } from '@/utils/string';
import { queryEncode, safeQueryDecode } from '@/utils/condition-query-encoding';
import { isEmptyFiltersQuery } from '../utils/list-params';
import { usePersistedColumnSources } from '../use-people-columns';
import { ObjectsExportModal, type ObjectsExportResult } from './objects-export-modal';
import { ExportOtherModal } from './export-other-modal';
import { ExportProgressModal, type ExportProgressModalData } from './export-progress-modal';
import { ExportStatusModal } from './export-status-modal';

/** Always part of the export file, so always seeded into "Displayed attributes". */
const PERMANENT_PEOPLE_ATTRIBUTES = ['id', 'email', 'created_at', 'unsubscribed'];

/**
 * The most recent "Choose attributes" selection, shared with the embedded app
 * (same key), so the picker seeds consistently while both serve the page.
 */
const LAST_EXPORTED_ATTRIBUTES_KEY = 'lastExportedAttributes::people';

function getLastExportedAttributes(): string[] {
  try {
    const raw = localStorage.getItem(LAST_EXPORTED_ATTRIBUTES_KEY);
    if (raw == null || isEmptyString(raw)) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.flatMap((entry) => (typeof entry === 'string' ? [entry] : [])) : [];
  } catch {
    return [];
  }
}

function saveLastExportedAttributes(attributes: string[] | null) {
  if (attributes == null) return;
  try {
    localStorage.setItem(LAST_EXPORTED_ATTRIBUTES_KEY, JSON.stringify(attributes));
  } catch {
    // Storage full/unavailable — seeding is a convenience, not a requirement.
  }
}

type ExportKind = 'all' | 'displayed' | 'choose';

export type ExportToCsvProps = {
  workspaceId: number;
  /** Sample workspaces keep the default column set, so the seed does too. */
  isSampleWorkspace: boolean;
  /** The environment's sent attributes — the "Export other" modal's static option set. */
  sentAttributes: readonly string[];
  /** The API says there are results but hid them (sensitive-attribute filtering). */
  hasHiddenResults: boolean;
  /** The list has completed a load at least once. */
  isListLoaded: boolean;
  /** Which search mode is active — only an email-mode term joins the payload. */
  activeSearchBy: 'email' | 'filter';
  /** The email search term ('' when none). */
  email: string;
  /** The raw encoded `filters` URL param, or null when none. */
  filters: string | null;
};

type UsePeopleExportOptions = {
  workspaceId: number;
  isSampleWorkspace: boolean;
  /** The active email search term, or null when none. */
  emailCondition: string | null;
  /** The raw encoded `filters` URL param, or null when none. */
  filters: string | null;
};

/**
 * The export flow behind the dropdown: seeds and opens the configuration
 * modal, queues the export with the page's active search/filters, remembers
 * explicit picks, and reports the outcome.
 */
function usePeopleExport({ workspaceId, isSampleWorkspace, emailCondition, filters }: UsePeopleExportOptions) {
  const modals = useModals();
  const taxonomy = useProductTaxonomy();
  const qc = useQueryClient();
  const { account } = useAuth();

  // Same gating as the table's column sources: sample settings aren't real,
  // so the "Displayed attributes" seed falls back to the defaults there too.
  const { settingsColumns, workspaceColumns } = usePersistedColumnSources({
    workspaceId,
    isSampleAccess: account?.is_sample_account_access === true,
    isSampleWorkspace,
  });

  const objectTypesQuery = useQuery(getObjectTypesQueryOptions(workspaceId, qc));
  const objectTypes = useMemo(
    () => [...(objectTypesQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [objectTypesQuery.data],
  );

  const exportMutation = useMutation({
    mutationFn: exportCustomers,
    onSuccess: (_response, variables) => {
      // Remember the people attribute selection so the next "Choose
      // attributes" export seeds it (no-op for "all attributes", which is null).
      saveLastExportedAttributes(variables.attributes);
    },
  });
  const openProgressModal = (data: ExportProgressModalData) =>
    modals.open(ExportProgressModal, data, { shouldCloseOnUnmount: true });

  // Re-entrancy guard that stays live inside stale closures (the Retry
  // button's onRetry captures an old render's mutation snapshot, whose
  // `isPending` is frozen) — mirrors Ember's drop task.
  const isExportRunningRef = useRef(false);
  // The single progress-modal instance: repeated failures update it in place
  // instead of stacking new modals (Ember drives one modal from tracked state).
  const progressModalIdRef = useRef<ReturnType<typeof openProgressModal> | null>(null);

  /** The saved visible columns (falling back to the workspace set) plus the permanent file columns. */
  const displayedAttributes = () => {
    const columns = settingsColumns.length === 0 && workspaceColumns.length !== 0 ? workspaceColumns : settingsColumns;
    return [...new Set([...columns, ...PERMANENT_PEOPLE_ATTRIBUTES])];
  };

  const showExportFailed = (message: string, onRetry: () => void) => {
    const data: ExportProgressModalData = {
      workspaceId,
      title: `Export ${taxonomy.term('people.titlePlural')}`,
      lastTaskSuccess: false,
      lastError: message,
      onRetry,
    };
    const existingId = progressModalIdRef.current;
    if (existingId != null) {
      modals.update(existingId, data);
      return;
    }
    const id = openProgressModal(data);
    progressModalIdRef.current = id;
    void modals.waitForClose(id).then(() => {
      if (progressModalIdRef.current === id) progressModalIdRef.current = null;
    });
  };

  const startExport = (result: ObjectsExportResult) => {
    if (isExportRunningRef.current) return;
    isExportRunningRef.current = true;
    const input: ExportCustomersInput = {
      workspaceId,
      email: emailCondition,
      filters: filters == null || isEmptyFiltersQuery(filters) ? null : queryEncode(safeQueryDecode(filters)),
      attributes: result.attributes,
    };
    if (result.relationshipObjectAttributes != null) {
      input.relationship_object_attributes = result.relationshipObjectAttributes;
    }

    // Open the live-status modal immediately (exportId null → "preparing") so
    // there's feedback while the export is queued.
    const statusModalId = modals.open(
      ExportStatusModal,
      { workspaceId, exportId: null },
      { shouldCloseOnUnmount: true },
    );
    exportMutation.mutate(input, {
      onSettled: () => {
        isExportRunningRef.current = false;
      },
      onSuccess: (response) => {
        // Hand the queued export id to the modal so it can poll and offer download.
        modals.update(statusModalId, { exportId: String(response.export_id) });
      },
      onError: (error) => {
        // Close the status modal and fall back to the legacy progress modal in
        // its error state; Retry re-runs the whole flow.
        modals.close(statusModalId);
        showExportFailed(error.message, () => {
          startExport(result);
        });
      },
    });
  };

  // Seed the modal per dropdown item: All -> all attributes (null), Displayed ->
  // the displayed columns, Choose -> the user's last saved picks if any, else
  // the modal default.
  return (kind: ExportKind) => {
    let initialPeopleAttributes: string[] | null | undefined;
    if (kind === 'all') {
      initialPeopleAttributes = null;
    } else if (kind === 'displayed') {
      initialPeopleAttributes = displayedAttributes();
    } else {
      const last = getLastExportedAttributes();
      initialPeopleAttributes = last.length > 0 ? last : undefined;
    }
    modals.open(
      ObjectsExportModal,
      { workspaceId, objectTypes, initialPeopleAttributes, onExport: startExport },
      { shouldCloseOnUnmount: true },
    );
  };
}

type UseExportOtherOptions = {
  workspaceId: number;
  sentAttributes: readonly string[];
};

/**
 * The "Export other" flow: opens the devices / duplicated-attribute modal,
 * then runs the export behind the legacy progress modal (pending → queued or
 * failed-with-Retry). Fire-and-forget: the file arrives by email and on the
 * exports page — no live status polling.
 */
function useExportOther({ workspaceId, sentAttributes }: UseExportOtherOptions) {
  const modals = useModals();
  const taxonomy = useProductTaxonomy();

  const exportMutation = useMutation({ mutationFn: exportOther });
  // Live re-entrancy guard for the Retry closure, whose mutation snapshot
  // freezes `isPending` — mirrors Ember's drop task.
  const isExportRunningRef = useRef(false);

  const startExportOther = (exportType: OtherExportType, attributes: string[], duplicatedAttribute: string | null) => {
    if (isExportRunningRef.current) return;
    const input: ExportOtherInput = { workspaceId, exportType, attributes, duplicatedAttribute };
    const modalId = modals.open(
      ExportProgressModal,
      {
        workspaceId,
        title: `Export ${taxonomy.term('people.titlePlural')}`,
        lastTaskSuccess: null,
        lastError: null,
        onRetry: () => run(),
      },
      { shouldCloseOnUnmount: true },
    );
    function run() {
      if (isExportRunningRef.current) return;
      isExportRunningRef.current = true;
      modals.update(modalId, { lastTaskSuccess: null, lastError: null });
      exportMutation.mutate(input, {
        onSettled: () => {
          isExportRunningRef.current = false;
        },
        onSuccess: () => modals.update(modalId, { lastTaskSuccess: true, lastError: null }),
        onError: (error) => modals.update(modalId, { lastTaskSuccess: false, lastError: error.message }),
      });
    }
    run();
  };

  return () => {
    modals.open(
      ExportOtherModal,
      { workspaceId, sentAttributes, onExport: startExportOther },
      {
        shouldCloseOnUnmount: true,
      },
    );
  };
}

/**
 * The "Export to CSV" header action: a dropdown whose items seed the export
 * configuration modal — All attributes, the displayed columns, or the user's
 * previous explicit picks — and queue the export.
 */
export function ExportToCsv({
  workspaceId,
  isSampleWorkspace,
  sentAttributes,
  hasHiddenResults,
  isListLoaded,
  activeSearchBy,
  email,
  filters,
}: ExportToCsvProps) {
  const { hasPermission } = usePermissions();

  const canCreateExports = hasPermission('journeys/exports/create');
  const isDisabled = !canCreateExports || hasHiddenResults || !isListLoaded;
  // Ember parity: the tooltip only shows when hidden results are the (co-)cause.
  const disabledTooltip =
    !canCreateExports && hasHiddenResults ? 'Contact an admin to access this feature.' : undefined;
  const emailCondition = activeSearchBy === 'email' && !isEmptyString(email) ? email : null;

  const openExportModal = usePeopleExport({ workspaceId, isSampleWorkspace, emailCondition, filters });
  const openExportOtherModal = useExportOther({ workspaceId, sentAttributes });

  return (
    <Tooltip content={disabledTooltip ?? ''} isDisabled={disabledTooltip == null}>
      <DropdownMenu>
        <DropdownMenuTriggerButton isDisabled={isDisabled} icon="chevron-down" iconPosition="trailing">
          Export to CSV
        </DropdownMenuTriggerButton>
        <DropdownMenuContent>
          <DropdownMenuItem onClick={() => openExportModal('all')}>All attributes</DropdownMenuItem>
          <DropdownMenuItem onClick={() => openExportModal('displayed')}>Displayed attributes</DropdownMenuItem>
          <DropdownMenuItem onClick={() => openExportModal('choose')}>Choose attributes</DropdownMenuItem>
          <DropdownMenuGroup>
            <DropdownMenuItem trailingIcon="chevron-right" onClick={() => openExportOtherModal()}>
              Export other
            </DropdownMenuItem>
          </DropdownMenuGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    </Tooltip>
  );
}
